import React, { useState, useEffect } from "react";
import {
  Grid,
  Box,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import { CrearItemServicio } from "../../utils/CrearItemServicio";
import { getAuthData } from "../../utils/auth";

const apiUrl = process.env.REACT_APP_API_URL;

const QUOTA_OPTIONS = [
  { label: 3, key: "cuota_1" },
  { label: 6, key: "cuota_2" },
  { label: 9, key: "cuota_3" },
  { label: 12, key: "cuota_4" },
  { label: 18, key: "cuota_5" },
];

const Funerarios = ({ agregarAlPresupuesto, resetTrigger, deshabilitarBoton }) => {
  const [planes, setPlanes] = useState([]);
  const [planSeleccionado, setPlanSeleccionado] = useState(null);
  const [cuotas, setCuotas] = useState(3);
  const [monto_por_cuotas, setMontoCuotas] = useState(0);
  const [modalidad_pago, setModalidadPago] = useState("");
  const [precio_plan, setPrecioPlan] = useState(0);
  const [contadoChecked, setContadoChecked] = useState(true);

  const auth = getAuthData();
  const token = auth?.token;

  const precioContado_bcv = planSeleccionado
    ? planSeleccionado.de_contado_bcv || 0
    : 0;
  const precioContado = planSeleccionado ? planSeleccionado.de_contado || 0 : 0;

  // Calcular cuotas disponibles
  const availableQuotas = React.useMemo(() => {
    if (!planSeleccionado) return [];
    return QUOTA_OPTIONS.filter((option) => {
      const val = planSeleccionado[option.key];
      return val !== null && val !== undefined && parseFloat(val) > 0;
    });
  }, [planSeleccionado]);

  // Effect to ensure valid quota selection
  useEffect(() => {
    if (modalidad_pago === "financiado") {
      if (availableQuotas.length > 0) {
        const isCurrentValid = availableQuotas.some((q) => q.label === cuotas);
        if (!isCurrentValid) {
          setCuotas(availableQuotas[0].label);
        }
      } else {
        setCuotas(""); // Avoid out-of-range error
      }
    }
  }, [modalidad_pago, availableQuotas, cuotas]);

  useEffect(() => {
    const fetchPlanes = async () => {
      try {

        const response = await axios.get(
          `${apiUrl}/api/planes/solicitudes?tipo_servicio=servicio-funerario`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const filteredPlanes = response.data.filter(
          (plan) => plan.is_active
        );
        setPlanes(filteredPlanes);
      } catch (error) {
        console.error("Error al obtener los planes:", error);
        toast.error("Error al cargar los planes de Funerarios.");
      }
    };
    fetchPlanes();
  }, []);

  useEffect(() => {
    setPlanSeleccionado(null);
    setModalidadPago("");
    setCuotas(3);
    setMontoCuotas(0);
    setContadoChecked(false);
  }, [resetTrigger]);

  const handlePlanChange = (e) => {
    const planId = e.target.value;
    const plan = planes.find((p) => p.id === planId);
    setPlanSeleccionado(plan);
    setPrecioPlan(0);
    setModalidadPago("");
    setCuotas(3);
    setMontoCuotas(0);
    setContadoChecked(false);
  };

  const handleModalidadChange = (e) => {
    setModalidadPago(e.target.value);
    setPrecioPlan(0);
    setCuotas(3);
    setMontoCuotas(0);
    setContadoChecked(false);
  };

  // Calcular montos
  useEffect(() => {
    if (!planSeleccionado || !modalidad_pago) {
      setPrecioPlan(0);
      setCuotas(3);
      setMontoCuotas(0);
      return;
    }

    if (modalidad_pago === "contado") {
      const precio = contadoChecked ? precioContado : precioContado_bcv;

      setPrecioPlan(precio);
      setMontoCuotas(0);
    }

    // Función que devuelve todos los estados listos para setear
    function calcularEstadosFinanciado({ cuotaMensual, totalPlan }) {
      return {
        montoCuotas: cuotaMensual,
        precioPlan: totalPlan,
      };
    }

    // -------------------------------
    // Bloque principal ultra limpio
    // -------------------------------
    // Bloque principal ultra limpio
    if (modalidad_pago === "financiado") {
      const selectedQuotaOption = QUOTA_OPTIONS.find((q) => q.label === cuotas);

      // En este caso, el valor en cuota_X representa el PRECIO TOTAL del plan financiado a X meses
      const precioTotalFinanciado = selectedQuotaOption && planSeleccionado
        ? parseFloat(planSeleccionado[selectedQuotaOption.key]) || 0
        : 0;

      // La cuota mensual se calcula dividiendo ese total entre el número de meses
      const cuotaMensual = cuotas > 0 ? precioTotalFinanciado / cuotas : 0;

      const estados = calcularEstadosFinanciado({
        cuotaMensual,
        totalPlan: precioTotalFinanciado,
      });

      // Aplicar todos los estados
      setMontoCuotas(estados.montoCuotas);
      setPrecioPlan(estados.precioPlan);
    }
  }, [modalidad_pago, cuotas, planSeleccionado, contadoChecked]);

  const handleAgregar = () => {
    if (!planSeleccionado || !modalidad_pago) {
      toast.error("Por favor, seleccione un plan y una modalidad.");
      return;
    }

    const item = CrearItemServicio({
      codigo: "FUN000001",
      tipo_servicio: "servicio-funerario",
      tipo_plan: "servicio",
      descripcion: planSeleccionado.descripcion,
      modalidad_pago,
      numero_cuotas: modalidad_pago === "financiado" ? cuotas : null,
      monto_por_cuota: modalidad_pago === "financiado" ? monto_por_cuotas : null,
      precio_plan,

      detalles_adicionales: {
        codigo: "FUN000001",
        tipo_servicio: "servicio-funerario",
        descripcion: planSeleccionado.descripcion,
      },
    });

    agregarAlPresupuesto(item);
  };

  return (
    <>
      <Typography
        sx={{
          marginBottom: "5px",
          fontFamily: "Bakbak One, sans-serif",
          color: "#0A4575",
          textAlign: "center",
        }}
        variant="h5"
      >
        SOLICITUD DE CONTRATO DE SERVICIO FUNERARIO
      </Typography>
      <Box>
        <Typography
          sx={{
            marginTop: "15px",
            marginBottom: "10px",
            fontFamily: "Bakbak One, sans-serif",
            color: "#0A4575",
          }}
          variant="h6"
        >
          PLAN A SOLICITAR
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <FormControl
            fullWidth
            size="small"
            variant="outlined"
            sx={{ boxShadow: 3 }}
          >
            <InputLabel>Seleccione el Plan</InputLabel>
            <Select
              label="Seleccione el Plan"
              value={planSeleccionado ? planSeleccionado.id : ""}
              onChange={handlePlanChange}
              autoComplete="off"
            >
              <MenuItem value="" disabled>
                Seleccione
              </MenuItem>
              {planes
                .filter(
                  (plan) => plan.is_active === true || plan.is_active === 1
                )
                .map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {plan.descripcion}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>

        {planSeleccionado && (
          <Grid item xs={2}>
            <FormControl
              fullWidth
              size="small"
              variant="outlined"
              sx={{ boxShadow: 3 }}
            >
              <InputLabel id="modalidad-label">Modalidad</InputLabel>
              <Select
                labelId="modalidad-label"
                id="modalidad"
                label="Modalidad"
                value={modalidad_pago}
                onChange={handleModalidadChange}
                autoComplete="off"
              >
                <MenuItem value="" disabled>
                  Seleccione
                </MenuItem>
                <MenuItem value="contado">De Contado</MenuItem>
                <MenuItem value="financiado">Financiado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}

        {planSeleccionado && modalidad_pago && (
          <>
            {modalidad_pago === "contado" && (
              <>
                <Grid item xs={2}>
                  <TextField
                    fullWidth
                    label="Precio del Plan"
                    variant="outlined"
                    size="small"
                    value={`$${precio_plan}`}
                    sx={{ boxShadow: 3 }}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={contadoChecked}
                        onChange={(e) => setContadoChecked(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Pago en Divisas"
                  />
                </Grid>
                <Grid item xs={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAgregar}
                    disabled={deshabilitarBoton}
                    fullWidth
                  >
                    Crear Solicitud
                  </Button>
                </Grid>
              </>
            )}

            {modalidad_pago === "financiado" && (
              <>
                <Grid item xs={2}>
                  <TextField
                    fullWidth
                    label="Precio del Plan"
                    variant="outlined"
                    size="small"
                    value={`$${precio_plan}`}
                    sx={{ boxShadow: 3 }}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={1}>
                  <FormControl
                    fullWidth
                    size="small"
                    variant="outlined"
                    sx={{ boxShadow: 3 }}
                  >
                    <InputLabel id="cuotas-label">Cuotas</InputLabel>
                    <Select
                      labelId="cuotas-label"
                      id="cuotas"
                      label="Cuotas"
                      value={cuotas}
                      onChange={(e) => setCuotas(parseInt(e.target.value) || 3)}
                      autoComplete="off"
                    >
                      {availableQuotas.length > 0 ? (
                        availableQuotas.map((option) => (
                          <MenuItem key={option.label} value={option.label}>
                            {option.label}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="" disabled>
                          Sin cuotas disponibles
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    fullWidth
                    label="Monto por Cuota"
                    variant="outlined"
                    size="small"
                    value={`$${monto_por_cuotas.toFixed(2)}`}
                    sx={{ boxShadow: 3 }}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAgregar}
                    disabled={deshabilitarBoton}
                    fullWidth
                  >
                    Crear Solicitud
                  </Button>
                </Grid>
              </>
            )}
          </>
        )}
      </Grid>
    </>
  );
};

export default Funerarios;
